import { useCallback, useSyncExternalStore } from 'react';

export type WorklistTheme = 'light' | 'dark';

const KEY = 'deeplabeling.theme';
const EVENT = 'deeplabeling-theme';

/** Where the choice lives when the browser refuses storage (a private window, a policy): this page only. */
let unstored: WorklistTheme = 'light';

function read(): WorklistTheme {
  try {
    return window.localStorage.getItem(KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return unstored;
  }
}

function subscribe(onChange: () => void) {
  window.addEventListener(EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}

/**
 * The worklist's theme, light by default: a choice of this browser, kept in its storage, shared by every
 * page that renders the worklist surface (and by other tabs). The viewer itself is always dark.
 */
export function useWorklistTheme(): [WorklistTheme, () => void] {
  const theme = useSyncExternalStore(subscribe, read, () => 'light' as const);
  const toggle = useCallback(() => {
    const next = read() === 'dark' ? 'light' : 'dark';
    try {
      window.localStorage.setItem(KEY, next);
    } catch {
      unstored = next;
    }
    window.dispatchEvent(new Event(EVENT));
  }, []);
  return [theme, toggle];
}
